import { writable, get } from "svelte/store";

interface SelectedDocument {
  document_id: string;
  file_name: string; // Blob storage name (was doc_path)
  doc_name: string; // User-friendly display name (was doc_title)
  doc_key: string; // Unique document identifier
  customer_code: string;
  project_id: string;
  project_name: string;
  [key: string]: any;
}

interface SelectedProject {
  project_id: string;
  project_name: string;
  document_count: number;
}

function createSelectedObjectsStore() {
  const store = writable<{
    documents: SelectedDocument[];
    projects: SelectedProject[];
  }>({
    documents: [],
    projects: [],
  });
  const { subscribe, set, update } = store;

  // Track selected and total document counts per project
  const projectSelectedCounts: Record<string, number> = {};
  const projectTotalCounts: Record<string, number | undefined> = {};

  return {
    subscribe,
    update,
    add: (document: SelectedDocument & { project_total_docs?: number }) =>
      update((state) => {
        // Check if document already exists - use doc_key for matching
        const existingDoc = state.documents.find((doc) => doc.doc_key === document.doc_key);
        if (existingDoc) {
          // Document already exists, don't modify counts
          return state;
        }

        // Add document
        const newDocuments = [...state.documents, document];

        // Update selected count for project
        projectSelectedCounts[document.project_id] = (projectSelectedCounts[document.project_id] || 0) + 1;

        // If total docs is provided, record it
        if (typeof document.project_total_docs === "number" && document.project_total_docs >= 0) {
          projectTotalCounts[document.project_id] = document.project_total_docs;
        }

        const selectedCount = projectSelectedCounts[document.project_id] || 0;
        const totalCount = projectTotalCounts[document.project_id];

        // Only consider a project selected if all documents are selected and total is known
        let newProjects = [...state.projects];
        const existingProjectIndex = newProjects.findIndex((p) => p.project_id === document.project_id);
        const isFullySelected = typeof totalCount === "number" && totalCount > 0 && selectedCount >= totalCount;

        if (isFullySelected) {
          if (existingProjectIndex === -1) {
            newProjects.push({
              project_id: document.project_id,
              project_name: document.project_name,
              document_count: selectedCount,
            });
          } else {
            newProjects[existingProjectIndex] = {
              ...newProjects[existingProjectIndex],
              document_count: selectedCount,
            };
          }
        }

        return {
          documents: newDocuments,
          projects: newProjects,
        };
      }),
    clearDocuments: () =>
      update((state) => {
        // Reset counters to ensure future selections start clean
        Object.keys(projectSelectedCounts).forEach((k) => delete projectSelectedCounts[k]);
        Object.keys(projectTotalCounts).forEach((k) => delete projectTotalCounts[k]);
        return {
          documents: [],
          projects: [],
        };
      }),
    remove: (docKey: string) =>
      update((state) => {
        // Find the document to be removed - use doc_key for matching
        const docToRemove = state.documents.find((doc) => doc.doc_key === docKey);
        if (!docToRemove) {
          // Document doesn't exist, no changes needed
          return state;
        }

        // Remove document
        const newDocuments = state.documents.filter((doc) => doc.doc_key !== docKey);

        // Update selected count for project
        projectSelectedCounts[docToRemove.project_id] = Math.max(0, (projectSelectedCounts[docToRemove.project_id] || 0) - 1);
        const selectedCount = projectSelectedCounts[docToRemove.project_id] || 0;
        const totalCount = projectTotalCounts[docToRemove.project_id];

        // If project exists and is no longer fully selected, remove it
        let newProjects = state.projects;
        if (!(typeof totalCount === "number" && totalCount > 0 && selectedCount >= totalCount)) {
          newProjects = state.projects.filter((project) => project.project_id !== docToRemove.project_id);
        }

        // If no documents left for any projects, clear everything
        if (newDocuments.length === 0) {
          // Reset counters
          Object.keys(projectSelectedCounts).forEach((k) => delete projectSelectedCounts[k]);
          Object.keys(projectTotalCounts).forEach((k) => delete projectTotalCounts[k]);
          return { documents: [], projects: [] };
        }

        return {
          documents: newDocuments,
          projects: newProjects,
        };
      }),
    removeProject: (projectId: string) =>
      update((state) => {
        const remainingDocuments = state.documents.filter((doc) => doc.project_id !== projectId);
        // Reset counters for this project
        delete projectSelectedCounts[projectId];
        delete projectTotalCounts[projectId];
        return {
          documents: remainingDocuments,
          projects: state.projects.filter((project) => project.project_id !== projectId),
        };
      }),
    has: (docKey: string) => {
      return get(store).documents.some((doc) => doc.doc_key === docKey);
    },
    hasProject: (projectId: string) => {
      return get(store).projects.some((p) => p.project_id === projectId);
    },
    getProjectDocumentCount: (projectId: string) => {
      return projectSelectedCounts[projectId] || 0;
    },
    clear: () => {
      // Reset counters to ensure future selections start clean
      Object.keys(projectSelectedCounts).forEach((k) => delete projectSelectedCounts[k]);
      Object.keys(projectTotalCounts).forEach((k) => delete projectTotalCounts[k]);
      set({ documents: [], projects: [] });
    },
  };
}

export const selectedObjects = createSelectedObjectsStore();
